import React, { useState, useEffect } from 'react';
import { IS_RECMAPPER } from './product';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import TrainingAdmin from './TrainingAdmin';
import CreditUsage from './CreditUsage';
import AnnouncementManager from './components/AnnouncementManager';
import AdaFeatureRequestsManager from './components/AdaFeatureRequestsManager';
import SubscriptionPanel from './components/SubscriptionPanel';
import ReportBrandingSettings from './components/ReportBrandingSettings';
import ApiKeysPanel from './components/ApiKeysPanel';

type SettingsTab = 'subscription' | 'integrations' | 'credit-usage' | 'account' | 'training' | 'announcements' | 'feature-requests';
// Atlas logo (their mark: a circle and a slanted bar), for the Integrations card.
const AtlasLogo: React.FC<{ grey?: boolean }> = ({ grey }) => (
  <svg viewBox="0 0 512 512" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Atlas">
    <rect width="512" height="512" rx="96" fill="#ffffff" stroke="#e2e8f0" strokeWidth="8" />
    <circle cx="163" cy="389" r="82" fill={grey ? '#9ca3af' : '#2e2b42'} />
    <path d="M284 151 L417 46 Q432 35 432 54 L432 458 Q432 470 420 470 L288 470 Q276 470 276 458 L276 166 Q276 157 284 151 Z" fill={grey ? '#d1d5db' : '#2e2b42'} />
  </svg>
);

// Giig logo (orange tile, white "g" with a dot), for the Integrations card.
const GiigLogo: React.FC<{ grey?: boolean }> = ({ grey }) => (
  <svg viewBox="0 0 300 300" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Giig">
    <rect width="300" height="300" rx="28" fill={grey ? '#d1d5db' : '#ff6600'} />
    <circle cx="146" cy="128" r="36" fill="none" stroke="#ffffff" strokeWidth="30" />
    <path d="M182 104 L182 170 Q182 222 132 224 Q104 225 92 212" fill="none" stroke="#ffffff" strokeWidth="30" strokeLinecap="round" />
    <circle cx="226" cy="64" r="15" fill="#ffffff" />
  </svg>
);

const SETTINGS_TABS: SettingsTab[] = ['subscription', 'integrations', 'credit-usage', 'account', 'training', 'announcements', 'feature-requests'];

const Settings: React.FC = () => {
  const { user, token, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<SettingsTab>('subscription');

  // Subscription state
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);
  const [showSubscriptionWarning, setShowSubscriptionWarning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // SourceWhale integration state
  const [sourcewhaleApiKey, setSourcewhaleApiKey] = useState('');
  const [sourcewhaleEmail, setSourcewhaleEmail] = useState('');
  const [sourcewhaleConnected, setSourcewhaleConnected] = useState(false);
  // lemlist integration state
  const [lemlistApiKey, setLemlistApiKey] = useState('');
  const [lemlistConnected, setLemlistConnected] = useState(false);
  const [lemlistWebhook, setLemlistWebhook] = useState<any | null>(null);

  // Athena CRM integration state
  const [athenaApiKey, setAthenaApiKey] = useState('');
  const [athenaTenant, setAthenaTenant] = useState('');
  const [athenaConnected, setAthenaConnected] = useState(false);

  // Spott ATS integration state. canConnect comes from the backend — while
  // the integration is in rollout only the platform admin can connect; other
  // accounts see the card greyed out as "Coming soon".
  const [spottApiKey, setSpottApiKey] = useState('');
  // Fathom notetaker (ATS/CRM Workspace) — section shows only when the ATS
  // module is enabled for this user (the status endpoint 403s otherwise).
  const [fathomVisible, setFathomVisible] = useState(false);
  const [fathomConnected, setFathomConnected] = useState(false);
  const [fathomLastEvent, setFathomLastEvent] = useState<string | null>(null);
  const [fathomApiKey, setFathomApiKey] = useState('');
  const [spottConnected, setSpottConnected] = useState(false);
  const [spottCanConnect, setSpottCanConnect] = useState(false);
  // Atlas ATS/CRM (like Spott): API key + the Atlas user records are added by.
  const [atlasConnected, setAtlasConnected] = useState(false);
  const [atlasCanConnect, setAtlasCanConnect] = useState(false);
  const [atlasApiKey, setAtlasApiKey] = useState('');
  const [atlasUserEmail, setAtlasUserEmail] = useState('');
  const [atlasLastSync, setAtlasLastSync] = useState<string | null>(null);
  // Giig ATS/CRM: API token + secret key (two headers on every Giig call).
  const [giigConnected, setGiigConnected] = useState(false);
  const [giigCanConnect, setGiigCanConnect] = useState(false);
  const [giigToken, setGiigToken] = useState('');
  const [giigSecret, setGiigSecret] = useState('');
  const [giigLastSync, setGiigLastSync] = useState<string | null>(null);
  const [giigSyncing, setGiigSyncing] = useState(false);
  const [atlasSyncing, setAtlasSyncing] = useState(false);

  // Anthropic (BYOK for web-researched AI columns)
  const [anthropicKey, setAnthropicKey] = useState('');
  const [anthropicConnected, setAnthropicConnected] = useState(false);
  const [webhookCopied, setWebhookCopied] = useState(false);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationSaving, setIntegrationSaving] = useState(false);
  const [canSkipValidation, setCanSkipValidation] = useState(false);

  // FullEnrich integration state
  const [fullenrichApiKey, setFullenrichApiKey] = useState('');
  const [fullenrichConnected, setFullenrichConnected] = useState(false);
  const [fullenrichLoading, setFullenrichLoading] = useState(false);
  const [fullenrichSaving, setFullenrichSaving] = useState(false);
  const [fullenrichHasTeam, setFullenrichHasTeam] = useState(false);
  const [fullenrichCanConfigure, setFullenrichCanConfigure] = useState(false);

  // Prospeo integration state (a team's own Prospeo key — migration 222)
  const [prospeoApiKey, setProspeoApiKey] = useState('');
  const [prospeoConnected, setProspeoConnected] = useState(false);
  const [prospeoLoading, setProspeoLoading] = useState(false);
  const [prospeoSaving, setProspeoSaving] = useState(false);
  const [prospeoHasTeam, setProspeoHasTeam] = useState(false);
  const [prospeoCanConfigure, setProspeoCanConfigure] = useState(false);

  // Cognism integration state
  const [cognismApiKey, setCognismApiKey] = useState('');
  const [cognismConnected, setCognismConnected] = useState(false);
  const [cognismLoading, setCognismLoading] = useState(false);
  const [cognismSaving, setCognismSaving] = useState(false);
  const [cognismHasTeam, setCognismHasTeam] = useState(false);
  const [cognismCanConfigure, setCognismCanConfigure] = useState(false);

  // Lusha integration state
  const [lushaApiKey, setLushaApiKey] = useState('');
  const [lushaConnected, setLushaConnected] = useState(false);
  const [lushaLoading, setLushaLoading] = useState(false);
  const [lushaSaving, setLushaSaving] = useState(false);
  const [lushaHasTeam, setLushaHasTeam] = useState(false);
  const [lushaCanConfigure, setLushaCanConfigure] = useState(false);

  // Apollo integration state
  const [apolloApiKey, setApolloApiKey] = useState('');
  const [apolloConnected, setApolloConnected] = useState(false);
  const [apolloLoading, setApolloLoading] = useState(false);
  const [apolloSaving, setApolloSaving] = useState(false);
  const [apolloHasTeam, setApolloHasTeam] = useState(false);
  const [apolloCanConfigure, setApolloCanConfigure] = useState(false);

  // ZoomInfo integration state
  const [zoomInfoUsername, setZoomInfoUsername] = useState('');
  const [zoomInfoPassword, setZoomInfoPassword] = useState('');
  // ZoomInfo supports two auth methods: 'basic' (username+password) and 'pki'
  // (username + client ID + RS256 private key, for enterprise/API-key accounts).
  const [zoomInfoAuthMethod, setZoomInfoAuthMethod] = useState<'basic' | 'pki'>('basic');
  const [zoomInfoClientId, setZoomInfoClientId] = useState('');
  const [zoomInfoPrivateKey, setZoomInfoPrivateKey] = useState('');
  const [zoomInfoConnected, setZoomInfoConnected] = useState(false);
  const [zoomInfoLoading, setZoomInfoLoading] = useState(false);
  const [zoomInfoSaving, setZoomInfoSaving] = useState(false);
  const [zoomInfoHasTeam, setZoomInfoHasTeam] = useState(false);
  const [zoomInfoCanConfigure, setZoomInfoCanConfigure] = useState(false);

  // Integration modal state
  const [integrationModal, setIntegrationModal] = useState<'sourcewhale' | 'lemlist' | 'fullenrich' | 'cognism' | 'prospeo' | 'lusha' | 'apollo' | 'zoominfo' | 'vincere' | 'athena' | 'spott' | 'atlas' | 'giig' | 'anthropic' | 'fathom' | null>(null);
  // A new integration window starts without messages left over from the page.
  useEffect(() => { if (integrationModal) { setError(null); setSuccessMessage(null); } }, [integrationModal]);

  // Vincere integration state
  const [vincereConnected, setVincereConnected] = useState(false);
  const [vincereTenant, setVincereTenant] = useState('');
  const [vincereApiKey, setVincereApiKey] = useState('');
  const [vincereClientId, setVincereClientId] = useState('');
  const [vincereClientSecret, setVincereClientSecret] = useState('');
  const [vincereLoading, setVincereLoading] = useState(false);
  const [vincereSaving, setVincereSaving] = useState(false);

  // Active enrichment provider toggle
  const [activeProvider, setActiveProvider] = useState<string | null>(null);
  const [providerSwitching, setProviderSwitching] = useState(false);

  // Clay webhook (admin only)
  const [clayWebhookUrl, setClayWebhookUrl] = useState('');
  const [clayWebhookConfigured, setClayWebhookConfigured] = useState(false);
  const [clayWebhookLoading, setClayWebhookLoading] = useState(false);
  const [clayWebhookSaving, setClayWebhookSaving] = useState(false);
  const [clayWebhookUsage, setClayWebhookUsage] = useState<{ count: number; limit: number; startedAt: string | null; alerted: boolean } | null>(null);
  const [clayContactsConfigured, setClayContactsConfigured] = useState(false);
  const [clayContactsUrl, setClayContactsUrl] = useState('');
  const [clayContactsSaving, setClayContactsSaving] = useState(false);

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordChanging, setPasswordChanging] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Cancel subscription state
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelDate, setCancelDate] = useState<string | null>(null);

  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');
  const tab = searchParams.get('tab');

  useEffect(() => {
    if (tab && SETTINGS_TABS.includes(tab as SettingsTab)) setActiveTab(tab as SettingsTab);
  }, [tab]);

  // Tabs live in the URL (?tab=…), so links, Back and lessons see which
  // tab is open (the Connect Claude lesson only shows on Integrations).
  const goTab = (t: SettingsTab) => {
    setActiveTab(t);
    navigate({ pathname: '/settings', search: `?tab=${t}` }, { replace: true });
  };

  useEffect(() => {
    if (success === 'true') {
      setSuccessMessage('Subscription successful! Your account is now active.');
      // Stay on whichever tab we landed on (subscription if returning from
      // Stripe checkout) and just clear the success/canceled params.
      navigate({ pathname: '/settings', search: tab ? `?tab=${tab}` : '' }, { replace: true });
    } else if (canceled === 'true') {
      setError('Subscription was canceled. You can try again when ready.');
      navigate({ pathname: '/settings', search: tab ? `?tab=${tab}` : '' }, { replace: true });
    }
  }, [success, canceled, navigate, tab]);

  // Handle Vincere OAuth callback redirect
  useEffect(() => {
    const vincereParam = searchParams.get('vincere');
    if (vincereParam === 'connected') {
      setSuccessMessage('Vincere connected successfully.');
      setActiveTab('integrations');
      fetchVincereStatus();
      navigate('/settings?tab=integrations', { replace: true });
    } else if (vincereParam === 'error') {
      const reason = searchParams.get('reason') || 'unknown error';
      setError(`Vincere connection failed: ${reason}`);
      setActiveTab('integrations');
      navigate('/settings?tab=integrations', { replace: true });
    }
  }, []);

  useEffect(() => {
    fetchSubscriptionStatus();
    fetchSourcewhaleStatus();
    fetchLemlistStatus();
    fetchAthenaStatus();
    fetchSpottStatus();
    fetchAtlasStatus();
    fetchGiigStatus();
    fetchFathomStatus();
    fetchAnthropicStatus();
    fetchFullenrichStatus();
    fetchCognismStatus();
    fetchProspeoStatus();
    fetchLushaStatus();
    fetchApolloStatus();
    fetchZoomInfoStatus();
    fetchActiveProvider();
    fetchVincereStatus();
    if (user?.role === 'admin') fetchClayWebhookStatus();
  }, []);

  const fetchSubscriptionStatus = async () => {
    try {
      setSubscriptionLoading(true);
      const response = await fetch('/api/stripe/subscription-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data.subscription_status);
        setHasStripeCustomer(data.has_stripe_customer);
        setShowSubscriptionWarning(data.show_subscription_warning || false);
      }
    } catch (err) {
      console.error('Failed to load subscription status');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchVincereStatus = async () => {
    try {
      setVincereLoading(true);
      const response = await fetch('/api/vincere/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVincereConnected(data.connected);
        if (data.tenant) setVincereTenant(data.tenant);
      }
    } catch (err) {
      console.error('Failed to load Vincere status');
    } finally {
      setVincereLoading(false);
    }
  };

  const handleConnectVincere = async () => {
    if (!vincereTenant.trim()) { setError('Please enter your Vincere tenant name'); return; }
    if (!vincereApiKey.trim()) { setError('Please enter your Vincere API key'); return; }
    if (!vincereClientId.trim()) { setError('Please enter your Vincere client ID'); return; }
    try {
      setVincereSaving(true);
      const response = await fetch('/api/vincere/initiate', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant: vincereTenant.trim(),
          apiKey: vincereApiKey.trim(),
          clientId: vincereClientId.trim(),
          clientSecret: vincereClientSecret.trim() || undefined,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.authUrl;
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to initiate Vincere connection');
      }
    } catch (err) {
      setError('Failed to connect Vincere');
    } finally {
      setVincereSaving(false);
    }
  };

  const handleDisconnectVincere = async () => {
    if (!window.confirm('Disconnect Vincere? This will remove your connection and all pulled staging data.')) return;
    try {
      await fetch('/api/vincere/disconnect', {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      setVincereConnected(false);
      setVincereTenant('');
      setSuccessMessage('Vincere disconnected');
    } catch (err) {
      setError('Failed to disconnect Vincere');
    }
  };

  const fetchSourcewhaleStatus = async () => {
    try {
      setIntegrationLoading(true);
      const response = await fetch('/api/sourcewhale/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setSourcewhaleConnected(data.connected);
        if (data.connected) {
          setSourcewhaleApiKey('••••••••••••••••'); // Masked
        }
      }
    } catch (err) {
      console.error('Failed to load SourceWhale status');
    } finally {
      setIntegrationLoading(false);
    }
  };

  const fetchFullenrichStatus = async () => {
    try {
      setFullenrichLoading(true);
      const response = await fetch('/api/fullenrich/team-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setFullenrichConnected(data.connected);
        setFullenrichHasTeam(data.hasTeam);
        setFullenrichCanConfigure(data.canConfigure);
        if (data.connected) {
          setFullenrichApiKey('••••••••••••••••'); // Masked
        }
      }
    } catch (err) {
      console.error('Failed to load FullEnrich status');
    } finally {
      setFullenrichLoading(false);
    }
  };

  const handleSubscribe = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await fetch('/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to start checkout');
      }
    } catch (err) {
      setError('Failed to start checkout process');
    } finally {
      setActionLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to open subscription portal');
      }
    } catch (err) {
      setError('Failed to open subscription portal');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      setError(null);

      const response = await fetch('/api/stripe/cancel-subscription', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCancelDate(new Date(data.cancel_at).toLocaleDateString());
        setShowCancelModal(false);
        setSuccessMessage(`Your subscription has been canceled. You'll have access until ${new Date(data.cancel_at).toLocaleDateString()}.`);
        fetchSubscriptionStatus();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to cancel subscription');
        setShowCancelModal(false);
      }
    } catch (err) {
      setError('Failed to cancel subscription');
      setShowCancelModal(false);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleSaveSourcewhaleKey = async (skipValidation = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);

      // Don't send masked value
      if (sourcewhaleApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setIntegrationSaving(false);
        return;
      }

      // Require email
      if (!sourcewhaleEmail.trim()) {
        setError('Please enter your SourceWhale email address');
        setIntegrationSaving(false);
        return;
      }

      const response = await fetch('/api/sourcewhale/api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: sourcewhaleApiKey, email: sourcewhaleEmail, skipValidation })
      });

      const data = await response.json();

      if (response.ok) {
        setSourcewhaleConnected(data.connected);
        setSuccessMessage(skipValidation
          ? 'Credentials saved (not validated). Test the connection once SourceWhale enables your API access.'
          : data.message);
        if (data.connected) {
          setSourcewhaleApiKey('••••••••••••••••');
          setSourcewhaleEmail('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save credentials');
        if (data.canSkipValidation) {
          setCanSkipValidation(true);
        }
      }
    } catch (err) {
      setError('Failed to save credentials');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleDisconnectSourcewhale = async () => {
    if (!window.confirm('Are you sure you want to disconnect SourceWhale?')) return;

    try {
      setIntegrationSaving(true);
      const response = await fetch('/api/sourcewhale/api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null, email: null })
      });

      if (response.ok) {
        setSourcewhaleConnected(false);
        setSourcewhaleApiKey('');
        setSourcewhaleEmail('');
        setSuccessMessage('SourceWhale disconnected');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const fetchLemlistStatus = async () => {
    try {
      const response = await fetch('/api/lemlist/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLemlistConnected(data.connected);
        if (data.connected) {
          setLemlistApiKey('••••••••••••••••'); // Masked
          fetchLemlistWebhookStatus();
        }
      }
    } catch (err) {
      console.error('Failed to load lemlist status');
    }
  };

  const fetchLemlistWebhookStatus = async () => {
    try {
      const response = await fetch('/api/lemlist/webhook-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setLemlistWebhook(await response.json());
    } catch (err) {
      console.error('Failed to load lemlist webhook status');
    }
  };

  const relativeTime = (iso: string): string => {
    const then = new Date(iso).getTime();
    const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const fetchAthenaStatus = async () => {
    try {
      const response = await fetch('/api/athena/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAthenaConnected(data.connected);
        if (data.connected) {
          setAthenaApiKey('••••••••••••••••');
          setAthenaTenant(data.tenant || '');
        }
      }
    } catch (err) {
      console.error('Failed to load Athena status');
    }
  };

  const fetchAtlasStatus = async () => {
    try {
      const response = await fetch('/api/atlas/status', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setAtlasConnected(!!data.connected);
        setAtlasCanConnect(!!data.canConnect);
        setAtlasUserEmail(data.userEmail || (user as any)?.email || '');
        setAtlasLastSync(data.lastSyncAt || null);
      }
    } catch (err) {
      console.error('Failed to load Atlas status');
    }
  };

  const handleSaveAtlas = async (disconnect = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      const response = await fetch('/api/atlas/connect', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(disconnect ? { apiKey: '' } : { apiKey: atlasApiKey.trim(), userEmail: atlasUserEmail.trim() })
      });
      const data = await response.json();
      if (response.ok) {
        setAtlasConnected(!!data.connected);
        setSuccessMessage(data.message);
        setAtlasApiKey('');
        if (data.userEmail) setAtlasUserEmail(data.userEmail);
      } else {
        setError(data.error || 'Failed to save Atlas settings');
      }
    } catch (err) {
      setError('Failed to save Atlas settings');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const fetchGiigStatus = async () => {
    try {
      const r = await fetch('/api/giig/status', { headers: { 'Authorization': `Bearer ${token}` } });
      if (r.ok) {
        const d = await r.json();
        setGiigConnected(!!d.connected); setGiigCanConnect(!!d.canConnect); setGiigLastSync(d.lastSyncAt || null);
      }
    } catch { /* card stays as is */ }
  };

  const handleSaveGiig = async (disconnect = false, skipValidation = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);
      const r = await fetch('/api/giig/connect', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(disconnect ? { token: '' } : { token: giigToken.trim(), secret: giigSecret.trim(), skipValidation }),
      });
      const d = await r.json();
      if (r.ok) { setGiigConnected(!!d.connected); setSuccessMessage(d.message); setGiigToken(''); setGiigSecret(''); }
      else { setError(d.error || 'Failed to save Giig settings'); if (d.canSkipValidation) setCanSkipValidation(true); }
    } catch { setError('Failed to save Giig settings'); }
    finally { setIntegrationSaving(false); }
  };

  const handleGiigSyncNow = async () => {
    setGiigSyncing(true);
    setError(null);
    try {
      const r = await fetch('/api/giig/sync-now', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      if (r.ok) { setSuccessMessage(d.message); fetchGiigStatus(); } else setError(d.error || 'Giig sync failed');
    } catch { setError('Giig sync failed'); }
    setGiigSyncing(false);
  };

  const handleAtlasSyncNow = async () => {
    setAtlasSyncing(true);
    setError(null);
    try {
      const r = await fetch('/api/atlas/sync-now', { method: 'POST', headers: { 'Authorization': `Bearer ${token}` } });
      const d = await r.json();
      if (r.ok) { setSuccessMessage(d.message); fetchAtlasStatus(); } else setError(d.error || 'Atlas sync failed');
    } catch { setError('Atlas sync failed'); }
    setAtlasSyncing(false);
  };

  const fetchSpottStatus = async () => {
    try {
      const response = await fetch('/api/spott/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSpottConnected(!!data.connected);
        setSpottCanConnect(!!data.canConnect);
        if (data.connected) setSpottApiKey('••••••••••••••••');
      }
    } catch (err) {
      console.error('Failed to load Spott status');
    }
  };

  const fetchFathomStatus = async () => {
    try {
      const response = await fetch('/api/ats/notetakers/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFathomVisible(true);
        setFathomConnected(!!data.fathom);
        setFathomLastEvent(data.fathom?.last_event_at || null);
      }
    } catch (err) { /* ATS not enabled for this user */ }
  };

  const handleFathom = async (disconnect: boolean) => {
    setIntegrationSaving(true);
    setError(null);
    try {
      const response = await fetch(`/api/ats/notetakers/fathom/${disconnect ? 'disconnect' : 'connect'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(disconnect ? {} : { apiKey: fathomApiKey })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');
      setFathomConnected(!disconnect);
      setFathomApiKey('');
      if (!disconnect) setIntegrationModal(null);
      fetchFathomStatus();
    } catch (e: any) {
      setError(e.message || 'Failed');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleFathomBackfill = async () => {
    const input = window.prompt('Backfill how many days of past Fathom meetings? (max 365)\n\nNotes file onto matched people (no duplicates); only meetings from the last 7 days also create task suggestions.', '90');
    if (!input) return;
    const days = Math.min(Math.max(parseInt(input, 10) || 0, 1), 365);
    if (!days) return;
    try {
      const response = await fetch('/api/ats/notetakers/fathom/backfill', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ days })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed');
      alert(`Backfilling ${days} days of Fathom meetings in the background — notes appear on matched records over the next few minutes.`);
    } catch (e: any) {
      setError(e.message || 'Failed to start backfill');
    }
  };

  const fetchAnthropicStatus = async () => {
    try {
      const response = await fetch('/api/ai-providers/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnthropicConnected(data.anthropicConnected);
        if (data.anthropicConnected) setAnthropicKey('••••••••••••••••');
      }
    } catch (err) {
      console.error('Failed to load Anthropic status');
    }
  };

  const handleSaveAnthropic = async (skipValidation = false, disconnect = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);
      if (!disconnect && anthropicKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setIntegrationSaving(false);
        return;
      }
      const response = await fetch('/api/ai-providers/anthropic-key', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(disconnect ? { apiKey: '' } : { apiKey: anthropicKey, skipValidation })
      });
      const data = await response.json();
      if (response.ok) {
        setAnthropicConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) setAnthropicKey('••••••••••••••••');
        else setAnthropicKey('');
      } else {
        setError(data.error || 'Failed to save Anthropic key');
        if (data.canSkipValidation) setCanSkipValidation(true);
      }
    } catch (err) {
      setError('Failed to save Anthropic key');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleSaveAthena = async (skipValidation = false, disconnect = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);
      if (!disconnect && athenaApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setIntegrationSaving(false);
        return;
      }
      const response = await fetch('/api/athena/api-key', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(disconnect
          ? { apiKey: '' }
          : { apiKey: athenaApiKey, tenant: athenaTenant, skipValidation })
      });
      const data = await response.json();
      if (response.ok) {
        setAthenaConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) setAthenaApiKey('••••••••••••••••');
        else { setAthenaApiKey(''); setAthenaTenant(''); }
      } else {
        setError(data.error || 'Failed to save Athena settings');
        if (data.canSkipValidation) setCanSkipValidation(true);
      }
    } catch (err) {
      setError('Failed to save Athena settings');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleSaveSpott = async (skipValidation = false, disconnect = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);
      if (!disconnect && spottApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setIntegrationSaving(false);
        return;
      }
      const response = await fetch('/api/spott/api-key', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(disconnect ? { apiKey: '' } : { apiKey: spottApiKey, skipValidation })
      });
      const data = await response.json();
      if (response.ok) {
        setSpottConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) setSpottApiKey('••••••••••••••••');
        else setSpottApiKey('');
      } else {
        setError(data.error || 'Failed to save Spott settings');
        if (data.canSkipValidation) setCanSkipValidation(true);
      }
    } catch (err) {
      setError('Failed to save Spott settings');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleSaveLemlistKey = async (skipValidation = false) => {
    try {
      setIntegrationSaving(true);
      setError(null);
      setCanSkipValidation(false);

      // Don't send masked value
      if (lemlistApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setIntegrationSaving(false);
        return;
      }

      const response = await fetch('/api/lemlist/api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: lemlistApiKey, skipValidation })
      });

      const data = await response.json();

      if (response.ok) {
        setLemlistConnected(data.connected);
        setSuccessMessage(skipValidation
          ? 'API key saved (not validated).'
          : data.message);
        if (data.connected) {
          setLemlistApiKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save API key');
        if (data.canSkipValidation) {
          setCanSkipValidation(true);
        }
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleDisconnectLemlist = async () => {
    if (!window.confirm('Are you sure you want to disconnect lemlist?')) return;

    try {
      setIntegrationSaving(true);
      const response = await fetch('/api/lemlist/api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null })
      });

      if (response.ok) {
        setLemlistConnected(false);
        setLemlistApiKey('');
        setSuccessMessage('lemlist disconnected');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setIntegrationSaving(false);
    }
  };

  const handleSaveFullenrichKey = async () => {
    try {
      setFullenrichSaving(true);
      setError(null);

      // Don't send masked value
      if (fullenrichApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setFullenrichSaving(false);
        return;
      }

      const response = await fetch('/api/fullenrich/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: fullenrichApiKey })
      });

      const data = await response.json();

      if (response.ok) {
        setFullenrichConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) {
          setFullenrichApiKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setFullenrichSaving(false);
    }
  };

  const handleDisconnectFullenrich = async () => {
    if (!window.confirm('Are you sure you want to disconnect FullEnrich? Your team will use credits for contact enrichment.')) return;

    try {
      setFullenrichSaving(true);
      const response = await fetch('/api/fullenrich/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null })
      });

      if (response.ok) {
        setFullenrichConnected(false);
        setFullenrichApiKey('');
        setSuccessMessage('FullEnrich disconnected. Your team will now use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setFullenrichSaving(false);
    }
  };

  // Prospeo integration handlers (same flow as Cognism)
  const fetchProspeoStatus = async () => {
    try {
      setProspeoLoading(true);
      const response = await fetch('/api/prospeo/team-status', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setProspeoConnected(data.connected);
        setProspeoHasTeam(data.hasTeam);
        setProspeoCanConfigure(data.canConfigure);
        if (data.connected) setProspeoApiKey('••••••••••••••••');
      }
    } catch (err) {
      console.error('Failed to load Prospeo status');
    } finally {
      setProspeoLoading(false);
    }
  };

  const handleSaveProspeoKey = async () => {
    try {
      setProspeoSaving(true);
      setError(null);
      if (prospeoApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        return;
      }
      const response = await fetch('/api/prospeo/team-api-key', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: prospeoApiKey }),
      });
      const data = await response.json();
      if (response.ok) {
        setProspeoConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) { setProspeoApiKey('••••••••••••••••'); setActiveProvider('prospeo'); }
      } else {
        setError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setProspeoSaving(false);
    }
  };

  const handleDisconnectProspeo = async () => {
    if (!window.confirm('Disconnect Prospeo? Contact enrichment will use credits.')) return;
    try {
      setProspeoSaving(true);
      const response = await fetch('/api/prospeo/team-api-key', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiKey: null }),
      });
      if (response.ok) {
        setProspeoConnected(false);
        setProspeoApiKey('');
        if (activeProvider === 'prospeo') setActiveProvider(null);
        setSuccessMessage('Prospeo disconnected. Contact enrichment will use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setProspeoSaving(false);
    }
  };

  // Cognism integration handlers
  const fetchCognismStatus = async () => {
    try {
      setCognismLoading(true);
      const response = await fetch('/api/cognism/team-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCognismConnected(data.connected);
        setCognismHasTeam(data.hasTeam);
        setCognismCanConfigure(data.canConfigure);
        if (data.connected) {
          setCognismApiKey('••••••••••••••••');
        }
      }
    } catch (err) {
      console.error('Failed to load Cognism status');
    } finally {
      setCognismLoading(false);
    }
  };

  const handleSaveCognismKey = async () => {
    try {
      setCognismSaving(true);
      setError(null);

      if (cognismApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setCognismSaving(false);
        return;
      }

      const response = await fetch('/api/cognism/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: cognismApiKey })
      });

      const data = await response.json();

      if (response.ok) {
        setCognismConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) {
          setCognismApiKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setCognismSaving(false);
    }
  };

  const handleDisconnectCognism = async () => {
    if (!window.confirm('Are you sure you want to disconnect Cognism? Contact enrichment will use credits.')) return;

    try {
      setCognismSaving(true);
      const response = await fetch('/api/cognism/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null })
      });

      if (response.ok) {
        setCognismConnected(false);
        setCognismApiKey('');
        setSuccessMessage('Cognism disconnected. Contact enrichment will use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setCognismSaving(false);
    }
  };

  // Lusha integration handlers
  const fetchLushaStatus = async () => {
    try {
      setLushaLoading(true);
      const response = await fetch('/api/lusha/team-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setLushaConnected(data.connected);
        setLushaHasTeam(data.hasTeam);
        setLushaCanConfigure(data.canConfigure);
        if (data.connected) {
          setLushaApiKey('••••••••••••••••');
        }
      }
    } catch (err) {
      console.error('Failed to load Lusha status');
    } finally {
      setLushaLoading(false);
    }
  };

  const handleSaveLushaKey = async () => {
    try {
      setLushaSaving(true);
      setError(null);

      if (lushaApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setLushaSaving(false);
        return;
      }

      const response = await fetch('/api/lusha/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: lushaApiKey })
      });

      const data = await response.json();

      if (response.ok) {
        setLushaConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) {
          setLushaApiKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setLushaSaving(false);
    }
  };

  const handleDisconnectLusha = async () => {
    if (!window.confirm('Are you sure you want to disconnect Lusha? Contact enrichment will use credits.')) return;

    try {
      setLushaSaving(true);
      const response = await fetch('/api/lusha/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null })
      });

      if (response.ok) {
        setLushaConnected(false);
        setLushaApiKey('');
        setSuccessMessage('Lusha disconnected. Contact enrichment will use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setLushaSaving(false);
    }
  };

  const fetchApolloStatus = async () => {
    try {
      setApolloLoading(true);
      const response = await fetch('/api/apollo/team-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setApolloConnected(data.connected);
        setApolloHasTeam(data.hasTeam);
        setApolloCanConfigure(data.canConfigure);
        if (data.connected) {
          setApolloApiKey('••••••••••••••••');
        }
      }
    } catch (err) {
      console.error('Failed to load Apollo status');
    } finally {
      setApolloLoading(false);
    }
  };

  const handleSaveApolloKey = async () => {
    try {
      setApolloSaving(true);
      setError(null);

      if (apolloApiKey === '••••••••••••••••') {
        setError('Please enter a new API key or disconnect');
        setApolloSaving(false);
        return;
      }

      const response = await fetch('/api/apollo/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: apolloApiKey })
      });

      const data = await response.json();

      if (response.ok) {
        setApolloConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) {
          setApolloApiKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save API key');
      }
    } catch (err) {
      setError('Failed to save API key');
    } finally {
      setApolloSaving(false);
    }
  };

  const handleDisconnectApollo = async () => {
    if (!window.confirm('Are you sure you want to disconnect Apollo? Contact enrichment will use credits.')) return;

    try {
      setApolloSaving(true);
      const response = await fetch('/api/apollo/team-api-key', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ apiKey: null })
      });

      if (response.ok) {
        setApolloConnected(false);
        setApolloApiKey('');
        setSuccessMessage('Apollo disconnected. Contact enrichment will use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setApolloSaving(false);
    }
  };

  // ZoomInfo handlers
  const fetchZoomInfoStatus = async () => {
    try {
      setZoomInfoLoading(true);
      const response = await fetch('/api/zoominfo/team-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setZoomInfoConnected(data.connected);
        setZoomInfoHasTeam(data.hasTeam);
        setZoomInfoCanConfigure(data.canConfigure);
        if (data.method === 'pki') setZoomInfoAuthMethod('pki');
        if (data.connected) {
          setZoomInfoUsername('••••••••••••••••');
          setZoomInfoPassword('••••••••••••••••');
          setZoomInfoClientId('••••••••••••••••');
          setZoomInfoPrivateKey('••••••••••••••••');
        }
      }
    } catch (err) {
      console.error('Failed to load ZoomInfo status');
    } finally {
      setZoomInfoLoading(false);
    }
  };

  const fetchActiveProvider = async () => {
    try {
      const response = await fetch('/api/fullenrich/active-provider', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setActiveProvider(data.activeProvider);
      }
    } catch (err) {
      console.error('Failed to fetch active provider');
    }
  };

  const toggleProvider = async (provider: string) => {
    const newProvider = activeProvider === provider ? null : provider;
    setProviderSwitching(true);
    try {
      const response = await fetch('/api/fullenrich/active-provider', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ provider: newProvider })
      });
      if (response.ok) {
        const data = await response.json();
        setActiveProvider(data.activeProvider);
        setSuccessMessage(data.message);
      }
    } catch (err) {
      setError('Failed to switch provider');
    } finally {
      setProviderSwitching(false);
    }
  };

  const handleSaveZoomInfoCredentials = async () => {
    try {
      setZoomInfoSaving(true);
      setError(null);

      if (zoomInfoUsername === '••••••••••••••••') {
        setError('Please enter your ZoomInfo credentials or disconnect');
        setZoomInfoSaving(false);
        return;
      }

      const body = zoomInfoAuthMethod === 'pki'
        ? { authMethod: 'pki', pkiUsername: zoomInfoUsername, clientId: zoomInfoClientId, privateKey: zoomInfoPrivateKey }
        : { authMethod: 'basic', username: zoomInfoUsername, password: zoomInfoPassword };

      const response = await fetch('/api/zoominfo/team-credentials', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (response.ok) {
        setZoomInfoConnected(data.connected);
        setSuccessMessage(data.message);
        if (data.connected) {
          setZoomInfoUsername('••••••••••••••••');
          setZoomInfoPassword('••••••••••••••••');
          setZoomInfoClientId('••••••••••••••••');
          setZoomInfoPrivateKey('••••••••••••••••');
        }
      } else {
        setError(data.error || 'Failed to save credentials');
      }
    } catch (err) {
      setError('Failed to save credentials');
    } finally {
      setZoomInfoSaving(false);
    }
  };

  const handleDisconnectZoomInfo = async () => {
    if (!window.confirm('Are you sure you want to disconnect ZoomInfo? Contact enrichment will use credits.')) return;

    try {
      setZoomInfoSaving(true);
      const response = await fetch('/api/zoominfo/team-credentials', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ connected: false })
      });

      if (response.ok) {
        setZoomInfoConnected(false);
        setZoomInfoUsername('');
        setZoomInfoPassword('');
        setSuccessMessage('ZoomInfo disconnected. Contact enrichment will use credits.');
      }
    } catch (err) {
      setError('Failed to disconnect');
    } finally {
      setZoomInfoSaving(false);
    }
  };

  // Clay webhook (admin only)
  const fetchClayWebhookStatus = async () => {
    try {
      setClayWebhookLoading(true);
      const response = await fetch('/api/clay/webhook-status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClayWebhookConfigured(data.configured);
        setClayWebhookUrl('');
      }
      // Also load usage counter (admin-only).
      const usageRes = await fetch('/api/clay/webhook-usage', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (usageRes.ok) {
        const usage = await usageRes.json();
        setClayWebhookUsage({
          count: usage.count ?? 0,
          limit: usage.limit ?? 50000,
          startedAt: usage.startedAt ?? null,
          alerted: usage.alerted ?? false
        });
      }
      // Contacts webhook (separate).
      const contactsRes = await fetch('/api/clay/contacts-webhook', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (contactsRes.ok) {
        const cd = await contactsRes.json();
        setClayContactsConfigured(cd.configured === true);
        setClayContactsUrl('');
      }
    } catch (err) {
      console.error('Failed to load Clay webhook status');
    } finally {
      setClayWebhookLoading(false);
    }
  };

  const handleSaveClayContactsWebhook = async () => {
    try {
      setClayContactsSaving(true);
      setError(null);
      const response = await fetch('/api/clay/contacts-webhook-url', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhookUrl: clayContactsUrl })
      });
      const data = await response.json();
      if (response.ok) {
        setClayContactsConfigured(data.configured);
        setClayContactsUrl('');
        setSuccessMessage(data.configured ? 'Contacts webhook URL saved.' : 'Contacts webhook URL cleared.');
      } else {
        setError(data.error || 'Failed to save contacts webhook URL');
      }
    } catch (err) {
      setError('Failed to save contacts webhook URL');
    } finally {
      setClayContactsSaving(false);
    }
  };

  const handleSaveClayWebhook = async () => {
    try {
      setClayWebhookSaving(true);
      setError(null);
      const response = await fetch('/api/clay/webhook-url', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ webhookUrl: clayWebhookUrl })
      });
      const data = await response.json();
      if (response.ok) {
        setClayWebhookConfigured(data.configured);
        setClayWebhookUrl('');
        setSuccessMessage(data.configured ? 'Webhook URL saved.' : 'Webhook URL cleared.');
      } else {
        setError(data.error || 'Failed to save webhook URL');
      }
    } catch (err) {
      setError('Failed to save webhook URL');
    } finally {
      setClayWebhookSaving(false);
    }
  };

  const getStatusBadge = () => {
    if (!subscriptionStatus || subscriptionStatus === 'none') {
      return <span className="px-3 py-1 text-sm bg-red-100 text-red-800 rounded-full">No Subscription</span>;
    } else if (subscriptionStatus === 'active') {
      return <span className="px-3 py-1 text-sm bg-green-100 text-green-800 rounded-full">Active</span>;
    } else if (subscriptionStatus === 'past_due') {
      return <span className="px-3 py-1 text-sm bg-yellow-100 text-yellow-800 rounded-full">Past Due</span>;
    } else if (subscriptionStatus === 'canceled') {
      return <span className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded-full">Canceled</span>;
    }
    return <span className="px-3 py-1 text-sm bg-gray-100 text-gray-800 rounded-full">{subscriptionStatus}</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/')}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Settings</h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                {user?.firstName} {user?.lastName}
              </span>
              <button onClick={logout} className="text-sm text-gray-500 hover:text-gray-700">
                Sign out
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6 max-md:-mx-3 max-md:px-3 max-md:overflow-x-auto">
          <nav className="-mb-px flex space-x-8 max-md:space-x-5">
            <button
              onClick={() => goTab('subscription')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeTab === 'subscription'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
              }`}
            >
              Subscription
            </button>
            {!IS_RECMAPPER && <button
              onClick={() => goTab('integrations')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeTab === 'integrations'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
              }`}
            >
              Integrations
            </button>}
            <button
              onClick={() => goTab('credit-usage')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeTab === 'credit-usage'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
              }`}
            >
              Credit Usage & Limits
            </button>
            <button
              onClick={() => goTab('account')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                activeTab === 'account'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
              }`}
            >
              Account
            </button>
            {user?.role === 'admin' && (
              <button
                onClick={() => goTab('training')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                  activeTab === 'training'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
                }`}
              >
                Training
              </button>
            )}
            {user?.id === 1 && (
              <button
                onClick={() => goTab('announcements')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                  activeTab === 'announcements'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
                }`}
              >
                Announcements
              </button>
            )}
            {user?.id === 1 && (
              <button
                onClick={() => goTab('feature-requests')}
                className={`pb-4 px-1 border-b-2 font-medium text-sm max-md:whitespace-nowrap max-md:flex-shrink-0 ${
                  activeTab === 'feature-requests'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 transition-all duration-200'
                }`}
              >
                Feature requests
              </button>
            )}
          </nav>
        </div>

        {/* Messages */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200/80 rounded-xl animate-fade-in">
            <p className="text-red-800">{error}</p>
            <button onClick={() => setError(null)} className="text-sm text-red-600 underline mt-1">Dismiss</button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200/80 rounded-xl animate-fade-in">
            <p className="text-green-800">{successMessage}</p>
            <button onClick={() => setSuccessMessage(null)} className="text-sm text-green-600 underline mt-1">Dismiss</button>
          </div>
        )}

        {/* Subscription Warning for grandfathered users */}
        {showSubscriptionWarning && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-300/80 rounded-xl">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="font-medium text-amber-800">Subscription Required</h3>
                <p className="text-amber-700 text-sm mt-1">
                  Your account does not currently have an active subscription. Please subscribe to MarketMapr Pro to ensure continued access to all features.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-4 tracking-tight">Subscription</h2>
            <SubscriptionPanel onError={setError} />
            <div className="border-t pt-6 mt-6">
              <h3 className="font-medium mb-2">Billing & Invoices</h3>
              <p className="text-gray-600 text-sm mb-4">
                View your payment history, download invoices, and manage your billing details.
              </p>
              <a
                href="https://billing.stripe.com/p/login/aEU6qXaGR88T8y49AA"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center px-6 py-3"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                View Invoices
              </a>
            </div>
          </div>
        )}

        {/* Integrations Tab */}
        {activeTab === 'integrations' && !IS_RECMAPPER && (
          <div>
            <ApiKeysPanel token={token} />
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Outreach tools</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* SourceWhale Card */}
              <button
                onClick={() => setIntegrationModal('sourcewhale')}
                className="card card card-hover p-5 text-left cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <img src="https://recmapper.com/wp-content/uploads/2026/02/3.png" alt="SourceWhale" className="w-12 h-12 rounded-lg object-cover" />
                  {sourcewhaleConnected ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                  )}
                </div>
                <h3 className="font-semibold text-sm">SourceWhale</h3>
                <p className="text-xs text-gray-500 mt-1">Multi-channel outreach sequences</p>
              </button>

              {/* lemlist Card */}
              <button
                onClick={() => setIntegrationModal('lemlist')}
                className="card card card-hover p-5 text-left cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="lemlist">
                    <rect width="100" height="100" rx="24" fill="#2F6BFF" />
                    <g fill="#ffffff">
                      <rect x="32" y="30" width="11" height="40" rx="5.5" />
                      <rect x="43" y="30" width="24" height="11" rx="5.5" />
                      <rect x="43" y="44.5" width="18" height="11" rx="5.5" />
                      <rect x="43" y="59" width="24" height="11" rx="5.5" />
                    </g>
                  </svg>
                  {lemlistConnected ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                  )}
                </div>
                <h3 className="font-semibold text-sm">lemlist</h3>
                <p className="text-xs text-gray-500 mt-1">Email outreach campaigns &amp; sequences</p>
              </button>

            </div>

            <h3 className="text-sm font-semibold text-gray-700 mt-8 mb-3">Data providers</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* FullEnrich Card */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('fullenrich')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="https://recmapper.com/wp-content/uploads/2026/02/2.png" alt="FullEnrich" className="w-12 h-12 rounded-lg object-cover" />
                    {fullenrichConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">FullEnrich</h3>
                  <p className="text-xs text-gray-500 mt-1">Contact data enrichment</p>
                </button>
                {fullenrichConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'fullenrich' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('fullenrich')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'fullenrich' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'fullenrich' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Cognism Card */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('cognism')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="https://recmapper.com/wp-content/uploads/2026/02/1.png" alt="Cognism" className="w-12 h-12 rounded-lg object-cover" />
                    {cognismConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Cognism</h3>
                  <p className="text-xs text-gray-500 mt-1">B2B contact data enrichment</p>
                </button>
                {cognismConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'cognism' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('cognism')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'cognism' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'cognism' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Prospeo Card — the team's own Prospeo account */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('prospeo')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="/integrations/prospeo.png" alt="Prospeo" className="w-12 h-12 rounded-lg object-cover" />
                    {prospeoConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Prospeo</h3>
                  <p className="text-xs text-gray-500 mt-1">Verified mobiles and work emails</p>
                </button>
                {prospeoConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'prospeo' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('prospeo')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'prospeo' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'prospeo' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Lusha Card */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('lusha')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="https://recmapper.com/wp-content/uploads/2026/02/Untitled-design-25.png" alt="Lusha" className="w-12 h-12 rounded-lg object-cover" />
                    {lushaConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Lusha</h3>
                  <p className="text-xs text-gray-500 mt-1">Contact data enrichment</p>
                </button>
                {lushaConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'lusha' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('lusha')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'lusha' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'lusha' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* Apollo Card */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('apollo')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="https://recmapper.com/wp-content/uploads/2026/02/apollo-logo.jpg" alt="Apollo" className="w-12 h-12 rounded-lg object-cover" />
                    {apolloConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Apollo</h3>
                  <p className="text-xs text-gray-500 mt-1">Contact data enrichment</p>
                </button>
                {apolloConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'apollo' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('apollo')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'apollo' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'apollo' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>

              {/* ZoomInfo Card */}
              <div className="card card-hover p-5 text-left">
                <button onClick={() => setIntegrationModal('zoominfo')} className="w-full text-left">
                  <div className="flex items-center justify-between mb-3">
                    <img src="https://recmapper.com/wp-content/uploads/2026/02/Zoominfo-logo.jpg" alt="ZoomInfo" className="w-12 h-12 rounded-lg object-cover" />
                    {zoomInfoConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">ZoomInfo</h3>
                  <p className="text-xs text-gray-500 mt-1">Contact data enrichment</p>
                </button>
                {zoomInfoConnected && user?.teamRole === 'admin' && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                    <span className="text-xs text-gray-500">{activeProvider === 'zoominfo' ? 'Active for enrichment' : 'Inactive'}</span>
                    <button
                      onClick={() => toggleProvider('zoominfo')}
                      disabled={providerSwitching}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${activeProvider === 'zoominfo' ? 'bg-green-500' : 'bg-gray-300'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${activeProvider === 'zoominfo' ? 'translate-x-[18px]' : 'translate-x-[3px]'}`} />
                    </button>
                  </div>
                )}
              </div>
            </div>


            {user?.teamRole === 'admin' && (
              <p className="mt-3 text-xs text-gray-400">
                {activeProvider
                  ? `Enrichment is using ${activeProvider.charAt(0).toUpperCase() + activeProvider.slice(1)}. Toggle off to switch to Credits.`
                  : 'No provider active — enrichment uses Credits.'}
              </p>
            )}

            <h3 className="text-sm font-semibold text-gray-700 mt-8 mb-3">AI</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Anthropic Card (BYOK) */}
              <button
                onClick={() => setIntegrationModal('anthropic')}
                className="card card card-hover p-5 text-left cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Anthropic">
                    <rect width="100" height="100" rx="24" fill="#191919" />
                    <path d="M42 28 L24 72 h10 l3.6-9.4 h18.8 L60 72 h10 L52 28 Z M41 54 l6-15.6 L53 54 Z" fill="#D97757" />
                    <path d="M58 28 h10 L86 72 h-10 Z" fill="#D97757" />
                  </svg>
                  {anthropicConnected ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                  )}
                </div>
                <h3 className="font-semibold text-sm">Anthropic (Claude)</h3>
                <p className="text-xs text-gray-500 mt-1">Your own API key powers web-researched AI columns</p>
              </button>
            </div>

            <h3 className="text-sm font-semibold text-gray-700 mt-8 mb-3">CRM / ATS</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Athena CRM Card */}
              <button
                onClick={() => setIntegrationModal('athena')}
                className="card card card-hover p-5 text-left cursor-pointer"
              >
                <div className="flex items-center justify-between mb-3">
                  <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Athena CRM">
                    <rect width="100" height="100" rx="24" fill="#0F172A" />
                    <path d="M50 22 L74 74 H62 L50 46 L38 74 H26 Z" fill="#38BDF8" />
                  </svg>
                  {athenaConnected ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                  )}
                </div>
                <h3 className="font-semibold text-sm">Athena CRM</h3>
                <p className="text-xs text-gray-500 mt-1">Two-way CRM sync &amp; monitoring signals</p>
              </button>

              {/* Spott ATS Card — connectable by the platform admin during
                  rollout; everyone else sees it greyed out as Coming soon */}
              {spottCanConnect ? (
                <button
                  onClick={() => setIntegrationModal('spott')}
                  className="card card-hover p-5 text-left cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-3">
                    <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Spott">
                      <rect width="100" height="100" rx="24" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" />
                      <circle cx="37.5" cy="49.4" r="29.4" fill="#0c1c2d" />
                      <circle cx="80.5" cy="66.6" r="12.4" fill="#f0610f" />
                    </svg>
                    {spottConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Spott</h3>
                  <p className="text-xs text-gray-500 mt-1">Push candidates with full career history to your ATS</p>
                </button>
              ) : (
                <div
                  className="card p-5 text-left opacity-50 cursor-not-allowed select-none"
                  title="Spott integration is coming soon"
                >
                  <div className="flex items-center justify-between mb-3">
                    <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Spott">
                      <rect width="100" height="100" rx="24" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" />
                      <circle cx="37.5" cy="49.4" r="29.4" fill="#9ca3af" />
                      <circle cx="80.5" cy="66.6" r="12.4" fill="#d1d5db" />
                    </svg>
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded-full">Coming soon</span>
                  </div>
                  <h3 className="font-semibold text-sm text-gray-500">Spott</h3>
                  <p className="text-xs text-gray-400 mt-1">Push candidates with full career history to your ATS</p>
                </div>
              )}

              {/* Atlas ATS/CRM Card — open to everyone (30 Oct 2026); the greyed
                  Coming soon card only shows if the server says it can't be connected */}
              {atlasCanConnect ? (
                <button
                  onClick={() => setIntegrationModal('atlas')}
                  className="card card-hover p-5 text-left cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-3">
                    <AtlasLogo />
                    {atlasConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Atlas</h3>
                  <p className="text-xs text-gray-500 mt-1">Two-way sync of candidates, companies and contacts with your ATS/CRM</p>
                </button>
              ) : (
                <div className="card p-5 text-left opacity-50 cursor-not-allowed select-none" title="Atlas integration is coming soon">
                  <div className="flex items-center justify-between mb-3">
                    <AtlasLogo grey />
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded-full">Coming soon</span>
                  </div>
                  <h3 className="font-semibold text-sm text-gray-500">Atlas</h3>
                  <p className="text-xs text-gray-400 mt-1">Two-way sync of candidates, companies and contacts with your ATS/CRM</p>
                </div>
              )}

              {/* Giig ATS/CRM Card — early access while it's tested */}
              {giigCanConnect ? (
                <button onClick={() => setIntegrationModal('giig')} className="card card-hover p-5 text-left cursor-pointer">
                  <div className="flex items-center justify-between mb-3">
                    <GiigLogo />
                    {giigConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Giig</h3>
                  <p className="text-xs text-gray-500 mt-1">Send candidates, companies and contacts to your ATS/CRM</p>
                </button>
              ) : (
                <div className="card p-5 text-left opacity-50 cursor-not-allowed select-none" title="Giig integration is coming soon">
                  <div className="flex items-center justify-between mb-3">
                    <GiigLogo grey />
                    <span className="px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded-full">Coming soon</span>
                  </div>
                  <h3 className="font-semibold text-sm text-gray-500">Giig</h3>
                  <p className="text-xs text-gray-400 mt-1">Send candidates, companies and contacts to your ATS/CRM</p>
                </div>
              )}

              {/* Vincere Card — greyed out until the integration actually works */}
              <div
                className="card p-5 text-left opacity-50 cursor-not-allowed select-none"
                title="Vincere integration is not available yet"
              >
                <div className="flex items-center justify-between mb-3">
                  <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-12 h-12 rounded-lg object-cover grayscale" />
                  <span className="px-2 py-1 text-xs bg-gray-100 text-gray-400 rounded-full">Coming soon</span>
                </div>
                <h3 className="font-semibold text-sm text-gray-500">Vincere</h3>
                <p className="text-xs text-gray-400 mt-1">Keep your CRM data fresh with enrichment</p>
              </div>
            </div>

            {/* Notetakers — meeting recorders whose notes flow onto ATS records */}
            {fathomVisible && (<>
              <h3 className="text-sm font-semibold text-gray-700 mt-8 mb-3">Notetakers</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <button
                  onClick={() => setIntegrationModal('fathom')}
                  className="card card-hover p-5 text-left cursor-pointer"
                >
                  <div className="flex items-center justify-between mb-3">
                    <img src="/integrations/fathom.png" alt="Fathom" className="w-12 h-12 rounded-lg object-cover" />
                    {fathomConnected ? (
                      <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">Connected</span>
                    ) : (
                      <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">Not connected</span>
                    )}
                  </div>
                  <h3 className="font-semibold text-sm">Fathom</h3>
                  <p className="text-xs text-gray-500 mt-1">Meeting notes file onto records; agreed outcomes become suggested tasks</p>
                </button>
              </div>
            </>)}

            {/* Admin: Company Enrichment Webhook */}
            {user?.role === 'admin' && (<>
              <div className="mt-6 card p-5">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  <h3 className="font-semibold text-sm">Company Enrichment Webhook</h3>
                  {clayWebhookConfigured ? (
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">Active</span>
                  ) : (
                    <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">Not set</span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mb-3">Webhook URL for company data enrichment. Only visible to system admins.</p>

                {/* Usage bar — Clay's 50k-per-webhook lifetime cap. Counter
                    resets to 0 only when the webhook URL is changed. */}
                {clayWebhookConfigured && clayWebhookUsage && (() => {
                  const pct = Math.min(100, Math.round((clayWebhookUsage.count / clayWebhookUsage.limit) * 100));
                  const colour = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-blue-500';
                  const sinceStr = clayWebhookUsage.startedAt
                    ? new Date(clayWebhookUsage.startedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                    : null;
                  return (
                    <div className="mb-3 p-3 bg-gray-50 rounded-md">
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="font-medium text-gray-700">Calls used{sinceStr ? ` (since ${sinceStr})` : ''}</span>
                        <span className="text-gray-600">
                          {clayWebhookUsage.count.toLocaleString('en-GB')} / {clayWebhookUsage.limit.toLocaleString('en-GB')}
                        </span>
                      </div>
                      <div className="w-full bg-white border border-gray-200 rounded-full h-1.5">
                        <div className={`h-1.5 rounded-full ${colour}`} style={{ width: `${pct}%` }} />
                      </div>
                      <p className="text-[11px] text-gray-500 mt-1.5">
                        Clay's free webhook tier allows 50,000 calls in total per webhook (not monthly). This counter resets only when you change the URL. We'll email you at 40,000.
                      </p>
                      {clayWebhookUsage.count >= 40000 && (
                        <p className="text-[11px] text-amber-600 font-medium mt-1">
                          ⚠️ Approaching the 50,000 cap — consider rotating to a new webhook URL soon.
                        </p>
                      )}
                    </div>
                  );
                })()}

                {clayWebhookLoading ? (
                  <p className="text-xs text-gray-400">Loading...</p>
                ) : clayWebhookConfigured && !clayWebhookUrl ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-400 font-mono tracking-wider">{'••••••••••••••••••••••••••••••••'}</span>
                    <button
                      onClick={() => { setClayWebhookUrl(''); setClayWebhookConfigured(false); }}
                      className="btn-secondary text-sm whitespace-nowrap"
                    >
                      Change
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={clayWebhookUrl}
                      onChange={(e) => setClayWebhookUrl(e.target.value)}
                      placeholder="Paste webhook URL here..."
                      className="flex-1 input text-sm"
                    />
                    <button
                      onClick={handleSaveClayWebhook}
                      disabled={clayWebhookSaving || !clayWebhookUrl.trim()}
                      className="btn-primary text-sm whitespace-nowrap"
                    >
                      {clayWebhookSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                )}
              </div>

              {/* Admin: Clay Contacts Webhook (Find Contacts) */}
              <div className="mt-4 p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-sm">Clay Contacts Webhook</h3>
                  {clayContactsConfigured
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not set</span>}
                </div>
                <p className="text-xs text-gray-500 mb-3">Powers the "Find Contacts" feature (maps hiring managers). Separate from the enrichment webhook. Only visible to system admins.</p>
                {clayWebhookLoading ? (
                  <p className="text-xs text-gray-400">Loading...</p>
                ) : clayContactsConfigured && !clayContactsUrl ? (
                  <div className="flex items-center gap-2">
                    <span className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-400 font-mono tracking-wider">{'••••••••••••••••••••••••••••••••'}</span>
                    <button onClick={() => { setClayContactsUrl(''); setClayContactsConfigured(false); }} className="btn-secondary text-sm whitespace-nowrap">Change</button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input type="text" value={clayContactsUrl} onChange={(e) => setClayContactsUrl(e.target.value)} placeholder="Paste contacts webhook URL here..." className="flex-1 input text-sm" />
                    <button onClick={handleSaveClayContactsWebhook} disabled={clayContactsSaving || !clayContactsUrl.trim()} className="btn-primary text-sm whitespace-nowrap">{clayContactsSaving ? 'Saving...' : 'Save'}</button>
                  </div>
                )}
              </div>
            </>)}

            {/* Integration Modals */}
            {integrationModal && (
              <div className="modal-backdrop p-4" onClick={() => setIntegrationModal(null)}>
                <div className="modal-dialog max-w-md w-full max-h-[90vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-5 border-b">
                    <div className="flex items-center gap-3">
                      {integrationModal === 'sourcewhale' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/3.png" alt="SourceWhale" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">SourceWhale</h3>
                            <p className="text-sm text-gray-500">Multi-channel outreach sequences</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'lemlist' && (
                        <>
                          <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="lemlist">
                            <rect width="100" height="100" rx="24" fill="#2F6BFF" />
                            <g fill="#ffffff">
                              <rect x="32" y="30" width="11" height="40" rx="5.5" />
                              <rect x="43" y="30" width="24" height="11" rx="5.5" />
                              <rect x="43" y="44.5" width="18" height="11" rx="5.5" />
                              <rect x="43" y="59" width="24" height="11" rx="5.5" />
                            </g>
                          </svg>
                          <div>
                            <h3 className="font-semibold text-lg">lemlist</h3>
                            <p className="text-sm text-gray-500">Email outreach campaigns &amp; sequences</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'anthropic' && (
                        <>
                          <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Anthropic">
                            <rect width="100" height="100" rx="24" fill="#191919" />
                            <path d="M42 28 L24 72 h10 l3.6-9.4 h18.8 L60 72 h10 L52 28 Z M41 54 l6-15.6 L53 54 Z" fill="#D97757" />
                            <path d="M58 28 h10 L86 72 h-10 Z" fill="#D97757" />
                          </svg>
                          <div>
                            <h3 className="font-semibold text-lg">Anthropic (Claude)</h3>
                            <p className="text-sm text-gray-500">Bring your own key for web-researched AI columns</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'athena' && (
                        <>
                          <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Athena CRM">
                            <rect width="100" height="100" rx="24" fill="#0F172A" />
                            <path d="M50 22 L74 74 H62 L50 46 L38 74 H26 Z" fill="#38BDF8" />
                          </svg>
                          <div>
                            <h3 className="font-semibold text-lg">Athena CRM</h3>
                            <p className="text-sm text-gray-500">Push records, sync contact details back, signal notes</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'spott' && (
                        <>
                          <svg viewBox="0 0 100 100" className="w-12 h-12 rounded-lg" xmlns="http://www.w3.org/2000/svg" aria-label="Spott">
                            <rect width="100" height="100" rx="24" fill="#ffffff" stroke="#e2e8f0" strokeWidth="2" />
                            <circle cx="37.5" cy="49.4" r="29.4" fill="#0c1c2d" />
                            <circle cx="80.5" cy="66.6" r="12.4" fill="#f0610f" />
                          </svg>
                          <div>
                            <h3 className="font-semibold text-lg">Spott</h3>
                            <p className="text-sm text-gray-500">Push candidates with full career history to your ATS</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'atlas' && (
                        <>
                          <AtlasLogo />
                          <div>
                            <h3 className="font-semibold text-lg">Atlas</h3>
                            <p className="text-sm text-gray-500">Push candidates, companies and contacts to your ATS/CRM</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'giig' && (
                        <>
                          <GiigLogo />
                          <div>
                            <h3 className="font-semibold text-lg">Giig</h3>
                            <p className="text-sm text-gray-500">Send candidates, companies and contacts to your ATS/CRM</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'fathom' && (
                        <>
                          <img src="/integrations/fathom.png" alt="Fathom" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Fathom</h3>
                            <p className="text-sm text-gray-500">AI meeting notes, filed straight onto your ATS records</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'fullenrich' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/2.png" alt="FullEnrich" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">FullEnrich</h3>
                            <p className="text-sm text-gray-500">Contact data enrichment</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'cognism' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/1.png" alt="Cognism" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Cognism</h3>
                            <p className="text-sm text-gray-500">B2B contact data enrichment</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'prospeo' && (
                        <>
                          <img src="/integrations/prospeo.png" alt="Prospeo" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Prospeo</h3>
                            <p className="text-sm text-gray-500">Verified mobiles and work emails</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'lusha' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/Untitled-design-25.png" alt="Lusha" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Lusha</h3>
                            <p className="text-sm text-gray-500">Contact data enrichment</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'apollo' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/apollo-logo.jpg" alt="Apollo" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Apollo</h3>
                            <p className="text-sm text-gray-500">Contact data enrichment</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'zoominfo' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/02/Zoominfo-logo.jpg" alt="ZoomInfo" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">ZoomInfo</h3>
                            <p className="text-sm text-gray-500">Contact data enrichment</p>
                          </div>
                        </>
                      )}
                      {integrationModal === 'vincere' && (
                        <>
                          <img src="https://recmapper.com/wp-content/uploads/2026/03/vincere-logo.jpeg" alt="Vincere" className="w-12 h-12 rounded-lg object-cover" />
                          <div>
                            <h3 className="font-semibold text-lg">Vincere</h3>
                            <p className="text-sm text-gray-500">ATS/CRM data refresh</p>
                          </div>
                        </>
                      )}
                    </div>
                    <button
                      onClick={() => setIntegrationModal(null)}
                      className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
                    >
                      &times;
                    </button>
                  </div>

                  {/* Modal Body */}
                  <div className="p-5">
                    {/* Messages show here too: the page's own banner sits behind
                        this window, so a failed connect looked like nothing
                        happened (Atlas, 30 Oct 2026). */}
                    {error && (
                      <div className="mb-4 p-3 bg-red-50 border border-red-200/80 rounded-lg text-sm text-red-800" role="alert">{error}</div>
                    )}
                    {successMessage && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200/80 rounded-lg text-sm text-green-800">{successMessage}</div>
                    )}
                    {/* SourceWhale Modal Content */}
                    {integrationModal === 'sourcewhale' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your SourceWhale account to send candidates and hiring managers directly into outreach sequences.
                        </p>

                        {integrationLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : sourcewhaleConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Your SourceWhale account is connected. You can send contacts directly from project tables.
                            </p>
                            <button
                              onClick={handleDisconnectSourcewhale}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect SourceWhale
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                SourceWhale Email
                              </label>
                              <input
                                type="email"
                                value={sourcewhaleEmail}
                                onChange={(e) => setSourcewhaleEmail(e.target.value)}
                                placeholder="Your email registered with SourceWhale"
                                className="input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                API Key
                              </label>
                              <input
                                type="password"
                                value={sourcewhaleApiKey}
                                onChange={(e) => setSourcewhaleApiKey(e.target.value)}
                                placeholder="Your SourceWhale API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Find your API key in SourceWhale under Settings → API
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveSourcewhaleKey(false)}
                              disabled={integrationSaving || !sourcewhaleApiKey.trim() || !sourcewhaleEmail.trim()}
                              className="w-full px-4 py-2 bg-[#1aa2d9] text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect SourceWhale'}
                            </button>
                            {canSkipValidation && (
                              <button
                                onClick={() => handleSaveSourcewhaleKey(true)}
                                disabled={integrationSaving}
                                className="w-full btn-secondary text-sm"
                              >
                                Save without testing (for pending API access)
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* lemlist Modal Content */}
                    {integrationModal === 'lemlist' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your lemlist account to push candidates and hiring managers straight into your email outreach campaigns. Custom &amp; AI-column values are sent as lemlist variables, so you can reference them (including AI-written messages) in your templates.
                        </p>

                        {lemlistConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Your lemlist account is connected. You can send contacts to a campaign directly from project tables.
                            </p>

                            {/* Inbound webhook — syncs opens/replies/bounces back onto campaign members */}
                            <div className="border border-gray-200 rounded-md p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <h4 className="text-sm font-semibold text-gray-800">Engagement webhook</h4>
                                {lemlistWebhook?.last ? (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-green-700">
                                    <span className="w-2 h-2 rounded-full bg-green-500" />
                                    Last event {relativeTime(lemlistWebhook.last.received_at)}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
                                    <span className="w-2 h-2 rounded-full bg-gray-300" />
                                    No events yet
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500">
                                Paste this URL into lemlist under Settings → Integrations → Webhooks so opens, replies, bounces &amp; unsubscribes sync back onto your campaign members.
                              </p>
                              <div className="flex items-center gap-2">
                                <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded px-2 py-1.5 truncate">
                                  {lemlistWebhook?.webhookUrl || 'https://app.marketmapr.com/api/lemlist/webhook'}
                                  {lemlistWebhook?.signingEnforced ? '?secret=YOUR_SECRET' : ''}
                                </code>
                                <button
                                  onClick={() => {
                                    const url = (lemlistWebhook?.webhookUrl || 'https://app.marketmapr.com/api/lemlist/webhook')
                                      + (lemlistWebhook?.signingEnforced ? '?secret=YOUR_SECRET' : '');
                                    navigator.clipboard?.writeText(url);
                                    setWebhookCopied(true);
                                    setTimeout(() => setWebhookCopied(false), 1500);
                                  }}
                                  className="text-xs btn-secondary px-2 py-1.5 whitespace-nowrap"
                                >
                                  {webhookCopied ? 'Copied' : 'Copy'}
                                </button>
                              </div>
                              {lemlistWebhook?.signingEnforced ? (
                                <p className="text-[11px] text-gray-500">
                                  🔒 Signing is enforced — replace <code>YOUR_SECRET</code> with the value of <code>LEMLIST_WEBHOOK_SECRET</code> on the server.
                                </p>
                              ) : (
                                <p className="text-[11px] text-gray-400">
                                  Tip: set <code>LEMLIST_WEBHOOK_SECRET</code> on the server to require a secret on this URL.
                                </p>
                              )}
                              {lemlistWebhook?.last && (
                                <p className="text-[11px] text-gray-500">
                                  Latest: <span className="font-medium">{lemlistWebhook.last.raw_type || 'event'}</span>
                                  {lemlistWebhook.last.milestone ? ` → ${lemlistWebhook.last.milestone}` : ' (unmapped)'}
                                  {`, matched ${lemlistWebhook.last.matched} member${lemlistWebhook.last.matched === 1 ? '' : 's'}`}
                                  {` · ${lemlistWebhook.counts?.last_24h || 0} in 24h`}
                                </p>
                              )}
                            </div>

                            <button
                              onClick={handleDisconnectLemlist}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect lemlist
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                API Key
                              </label>
                              <input
                                type="password"
                                value={lemlistApiKey}
                                onChange={(e) => setLemlistApiKey(e.target.value)}
                                placeholder="Your lemlist API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Find your API key in lemlist under Settings → Integrations → API
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveLemlistKey(false)}
                              disabled={integrationSaving || !lemlistApiKey.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#3355FF' }}
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect lemlist'}
                            </button>
                            {canSkipValidation && (
                              <button
                                onClick={() => handleSaveLemlistKey(true)}
                                disabled={integrationSaving}
                                className="w-full btn-secondary text-sm"
                              >
                                Save without testing
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Anthropic Modal Content */}
                    {integrationModal === 'anthropic' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your own Anthropic API key to enable <strong>web research</strong> on AI columns:
                          Claude searches the web (up to 4 searches per row) and returns answers with sources and a
                          confidence rating. All usage is billed to your Anthropic account directly — no MarketMapr
                          credits are used.
                        </p>
                        {anthropicConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Anthropic is connected. The "Web research" option is now available when creating AI columns.
                            </p>
                            <button
                              onClick={() => handleSaveAnthropic(false, true)}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Anthropic
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                              <input
                                type="password"
                                value={anthropicKey}
                                onChange={(e) => setAnthropicKey(e.target.value)}
                                placeholder="sk-ant-…"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Create a key at console.anthropic.com → API Keys. Usage (tokens + web searches) is billed
                                to your Anthropic account at their standard rates.
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveAnthropic(false)}
                              disabled={integrationSaving || !anthropicKey.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#191919' }}
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect Anthropic'}
                            </button>
                            {canSkipValidation && (
                              <button
                                onClick={() => handleSaveAnthropic(true)}
                                disabled={integrationSaving}
                                className="w-full btn-secondary text-sm"
                              >
                                Save without testing
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Athena Modal Content */}
                    {integrationModal === 'athena' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect Athena CRM to push candidates from your projects, sync contact details added in Athena back into MarketMapr, and post monitoring signals (job changes, backfills) as notes on the linked Athena record.
                        </p>
                        {athenaConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Athena is connected (tenant: <span className="font-medium">{athenaTenant}</span>). Push candidates from any project table; contact details added in Athena sync back every 15 minutes.
                            </p>
                            <button
                              onClick={() => handleSaveAthena(false, true)}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Athena
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                              <input
                                type="password"
                                value={athenaApiKey}
                                onChange={(e) => setAthenaApiKey(e.target.value)}
                                placeholder="Your Athena API key"
                                className="input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tenant (organisation handle)</label>
                              <input
                                type="text"
                                value={athenaTenant}
                                onChange={(e) => setAthenaTenant(e.target.value)}
                                placeholder="your-organisation"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Both come from your Athena organisation administrator (Organization Settings → API keys). The key needs candidates view/update and note permissions.
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveAthena(false)}
                              disabled={integrationSaving || !athenaApiKey.trim() || !athenaTenant.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#0F172A' }}
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect Athena'}
                            </button>
                            {canSkipValidation && (
                              <button
                                onClick={() => handleSaveAthena(true)}
                                disabled={integrationSaving}
                                className="w-full btn-secondary text-sm"
                              >
                                Save without testing
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Spott Modal Content */}
                    {integrationModal === 'spott' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect Spott to push candidates from any project table straight into your Spott
                          workspace — full career history, education, locations and contact details included.
                          Duplicates (by LinkedIn URL, email or phone) are rejected by Spott, so pushes are
                          always safe to repeat.
                        </p>
                        {spottConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Spott is connected. Select candidates in any project table and use
                              “Send to Spott” to push them into your workspace.
                            </p>
                            <button
                              onClick={() => handleSaveSpott(false, true)}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Spott
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
                              <input
                                type="password"
                                value={spottApiKey}
                                onChange={(e) => setSpottApiKey(e.target.value)}
                                placeholder="Your Spott API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                From your Spott workspace settings (API keys). The key is validated with a
                                read-only call before saving.
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveSpott(false)}
                              disabled={integrationSaving || !spottApiKey.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#0c1c2d' }}
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect Spott'}
                            </button>
                            {canSkipValidation && (
                              <button
                                onClick={() => handleSaveSpott(true)}
                                disabled={integrationSaving}
                                className="w-full btn-secondary text-sm"
                              >
                                Save without testing
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Atlas Modal Content */}
                    {integrationModal === 'atlas' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect Atlas to send records from any project table into your Atlas account: candidates
                          (with contact details, current role, location, work history and education), companies, and
                          hiring managers as company contacts. Anyone already in Atlas (matched by email, LinkedIn or
                          phone) is linked, never overwritten, so sending again is always safe.
                        </p>
                        {atlasConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Atlas is connected{atlasUserEmail ? <> — records are added as <strong>{atlasUserEmail}</strong></> : ''}. Select
                              rows in any project table and use “Send to Atlas”. Each project gets a “MarketMapr – project
                              name” list in Atlas so you can see where people came from.
                            </p>
                            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md space-y-1">
                              <p><strong>Two-way:</strong> monitoring keeps people you’ve sent up to date in Atlas (role, company,
                                headline, summary, skills, work history) and notes each change on their Atlas timeline.</p>
                              <p>Every 15 minutes, Atlas activity comes back onto their MarketMapr Notes &amp; Activity: notes and
                                logged calls, calls and meetings (with Atlas AI notes), interviews, emails, job stages and rejections,
                                placements — and emails / phone numbers added in Atlas fill any gaps here.</p>
                              <div className="flex items-center justify-between pt-1">
                                <span className="text-xs text-gray-500">
                                  {atlasLastSync ? `Last synced ${new Date(atlasLastSync).toLocaleString('en-GB')}` : 'Not synced yet'}
                                </span>
                                <button onClick={handleAtlasSyncNow} disabled={atlasSyncing}
                                  className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-50">
                                  {atlasSyncing ? 'Syncing…' : 'Sync now'}
                                </button>
                              </div>
                            </div>
                            <button
                              onClick={() => handleSaveAtlas(true)}
                              disabled={integrationSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Atlas
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API key</label>
                              <input
                                type="password"
                                value={atlasApiKey}
                                onChange={(e) => setAtlasApiKey(e.target.value)}
                                placeholder="Your Atlas API key"
                                className="input text-sm"
                              />
                              <div className="mt-2 text-xs text-gray-600 bg-gray-50 rounded-md p-3">
                                <div className="font-medium text-gray-700 mb-1">Getting your Atlas API key</div>
                                <ol className="list-decimal pl-4 space-y-0.5">
                                  <li>In Atlas, open <strong>Settings → Company settings → Developers</strong>.</li>
                                  <li>Press <strong>Create a new API key</strong>.</li>
                                  <li>Click the <strong>…</strong> next to the new key and choose to <strong>edit</strong> it.</li>
                                  <li>Set <strong>every permission to Read-write</strong> and save. (Without this, connecting fails.)</li>
                                  <li>Copy the API key and paste it in the box above.</li>
                                </ol>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Your Atlas login email</label>
                              <input
                                type="email"
                                value={atlasUserEmail}
                                onChange={(e) => setAtlasUserEmail(e.target.value)}
                                placeholder="you@agency.com"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Atlas records everyone we add as added by this user, so it must be someone on your Atlas account.
                              </p>
                            </div>
                            <button
                              onClick={() => handleSaveAtlas(false)}
                              disabled={integrationSaving || !atlasApiKey.trim() || !atlasUserEmail.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#111827' }}
                            >
                              {integrationSaving ? 'Connecting...' : 'Connect Atlas'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Giig Modal Content */}
                    {integrationModal === 'giig' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect Giig to send records from any project table: candidates (contact details, role, location,
                          skills, summary, and their career history in a note), companies, and hiring managers as contacts.
                          Anyone already in Giig (same LinkedIn profile or email) is linked, not duplicated.
                        </p>
                        {giigConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Giig is connected. Select rows in any project table and use “Send to Giig”. Records are tagged
                              “MarketMapr” and the project name.
                            </p>
                            <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md space-y-1">
                              <p><strong>Kept up to date:</strong> monitoring updates people you’ve sent (role, summary, skills) and logs
                                each change as a note on their Giig record. Every 15 minutes, emails and phone numbers added in Giig fill
                                any gaps here.</p>
                              <div className="flex items-center justify-between pt-1">
                                <span className="text-xs text-gray-500">{giigLastSync ? `Last synced ${new Date(giigLastSync).toLocaleString('en-GB')}` : 'Not synced yet'}</span>
                                <button onClick={handleGiigSyncNow} disabled={giigSyncing}
                                  className="text-xs px-3 py-1 rounded border border-gray-300 hover:bg-white disabled:opacity-50">
                                  {giigSyncing ? 'Syncing…' : 'Sync now'}
                                </button>
                              </div>
                            </div>
                            <button onClick={() => handleSaveGiig(true)} disabled={integrationSaving} className="text-sm text-red-600 hover:text-red-800">
                              Disconnect Giig
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API token</label>
                              <input type="password" value={giigToken} onChange={(e) => setGiigToken(e.target.value)} placeholder="Long code starting eyJ…" className="input text-sm" />
                              <p className="mt-1 text-xs text-gray-500">The API_TOKEN from your Giig settings: a long code in three parts separated by dots. Copy all of it.</p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Secret / session key</label>
                              <input type="password" value={giigSecret} onChange={(e) => setGiigSecret(e.target.value)} placeholder="Your Giig secret key" className="input text-sm" />
                              <p className="mt-1 text-xs text-gray-500">Both are in your Giig account settings. Ask Giig to switch on “sensitive endpoint” access for the
                                key, so we can check for people already in Giig and keep records up to date.</p>
                            </div>
                            <button onClick={() => handleSaveGiig(false)} disabled={integrationSaving || !giigToken.trim() || !giigSecret.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm" style={{ background: '#ff6600' }}>
                              {integrationSaving ? 'Connecting...' : 'Connect Giig'}
                            </button>
                            {canSkipValidation && (
                              <button onClick={() => handleSaveGiig(false, true)} disabled={integrationSaving} className="w-full btn-secondary text-sm">
                                Save without testing
                              </button>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* Fathom Modal Content */}
                    {integrationModal === 'fathom' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your Fathom account and every recorded meeting's notes file automatically
                          onto the matched person's record in your ATS/CRM. Agreed action items become
                          suggested tasks (Jev reads the summary when there are none), and the manual
                          "how did it go?" prompt skips meetings Fathom already covered.
                        </p>
                        {fathomConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Fathom is connected — a webhook was created in your Fathom account automatically.
                              {fathomLastEvent
                                ? ` Last meeting received: ${new Date(fathomLastEvent).toLocaleString('en-GB')}.`
                                : ' Record a short meeting to test: the notes arrive here a few minutes after it ends.'}
                            </p>
                            <button
                              onClick={handleFathomBackfill}
                              className="w-full btn-secondary text-sm"
                            >
                              Backfill past meetings…
                            </button>
                            <button
                              onClick={() => handleFathom(true)}
                              disabled={integrationSaving}
                              className="w-full btn-secondary text-sm text-red-600"
                            >
                              {integrationSaving ? 'Disconnecting…' : 'Disconnect Fathom (removes the webhook)'}
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API key</label>
                              <input
                                type="password"
                                value={fathomApiKey}
                                onChange={(e) => setFathomApiKey(e.target.value)}
                                placeholder="Your Fathom API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Generate it in Fathom under Settings → API Access. The key is validated with a
                                read-only call, then MarketMapr creates the webhook in your Fathom account for
                                you — signed deliveries only, nothing to copy-paste.
                              </p>
                            </div>
                            <button
                              onClick={() => handleFathom(false)}
                              disabled={integrationSaving || !fathomApiKey.trim()}
                              className="w-full px-4 py-2 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 text-sm"
                              style={{ background: '#111827' }}
                            >
                              {integrationSaving ? 'Connecting…' : 'Connect Fathom'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* FullEnrich Modal Content */}
                    {integrationModal === 'fullenrich' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your team's FullEnrich account to use your own API credits for finding work emails, personal emails, and phone numbers.
                        </p>

                        {fullenrichLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
                          </div>
                        ) : !fullenrichHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">
                              You need to be part of a team to configure FullEnrich. Create or join a team first.
                            </p>
                          </div>
                        ) : !fullenrichCanConfigure ? (
                          <div className="space-y-3">
                            {fullenrichConnected ? (
                              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                                Your team has connected their FullEnrich account. Contact enrichment will use your team's FullEnrich credits.
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                Only team admins can configure the FullEnrich integration. Contact your team admin to set this up.
                              </p>
                            )}
                          </div>
                        ) : fullenrichConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Your team's FullEnrich account is connected. Contact enrichment (work email, personal email, phone) will be billed directly to your FullEnrich account.
                            </p>
                            <button
                              onClick={handleDisconnectFullenrich}
                              disabled={fullenrichSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect FullEnrich
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                FullEnrich API Key
                              </label>
                              <input
                                type="password"
                                value={fullenrichApiKey}
                                onChange={(e) => setFullenrichApiKey(e.target.value)}
                                placeholder="Your FullEnrich API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Find your API key in FullEnrich under Settings → API Keys
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Note:</strong> This integration is for contact data only (work email, personal email, phone numbers).
                                Company/candidate enrichment uses separate providers.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveFullenrichKey}
                              disabled={fullenrichSaving || !fullenrichApiKey.trim()}
                              className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {fullenrichSaving ? 'Connecting...' : 'Connect FullEnrich'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Cognism Modal Content */}
                    {integrationModal === 'cognism' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your Cognism account for instant contact enrichment. Find work emails and direct dial phone numbers using your Cognism credits.
                        </p>

                        {cognismLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : !cognismHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">
                              You need to be part of a team to configure Cognism. Create or join a team first.
                            </p>
                          </div>
                        ) : !cognismCanConfigure ? (
                          <div className="space-y-3">
                            {cognismConnected ? (
                              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                                Your team has connected Cognism. Contact enrichment will use Cognism with instant results.
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                Only team admins can configure the Cognism integration. Contact your team admin to set this up.
                              </p>
                            )}
                          </div>
                        ) : cognismConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Cognism is connected and active. Contact enrichment will return results instantly (no waiting). Credits are billed directly to your Cognism account.
                            </p>
                            <button
                              onClick={handleDisconnectCognism}
                              disabled={cognismSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Cognism
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cognism API Token
                              </label>
                              <input
                                type="password"
                                value={cognismApiKey}
                                onChange={(e) => setCognismApiKey(e.target.value)}
                                placeholder="Your Cognism API token"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Generate your API token in Cognism under Profile Settings → API Tokens
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Key benefits:</strong> Instant results (no waiting), 1 credit per contact, verified emails with quality scores, and direct dial phone numbers with DNC flags.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveCognismKey}
                              disabled={cognismSaving || !cognismApiKey.trim()}
                              className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {cognismSaving ? 'Connecting...' : 'Connect Cognism'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Prospeo Modal Content */}
                    {integrationModal === 'prospeo' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Already have a Prospeo account? Connect it and your team's phone and work-email searches use your Prospeo credits instead of MarketMapr credits.
                        </p>
                        {prospeoLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                          </div>
                        ) : !prospeoHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">You need to be part of a team to configure Prospeo. Create or join a team first.</p>
                          </div>
                        ) : !prospeoCanConfigure ? (
                          <p className={`text-sm p-3 rounded-md ${prospeoConnected ? 'text-green-700 bg-green-50' : 'text-gray-600 bg-gray-50'}`}>
                            {prospeoConnected
                              ? 'Your team has connected Prospeo. Phone and work-email searches use your Prospeo account.'
                              : 'Only team admins can connect Prospeo. Ask your team admin to set this up.'}
                          </p>
                        ) : prospeoConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Prospeo is connected. While it's switched on, phone and work-email searches return instantly and are billed to your Prospeo account — Prospeo only charges when it finds something. Personal emails aren't available from Prospeo.
                            </p>
                            <button onClick={handleDisconnectProspeo} disabled={prospeoSaving} className="text-sm text-red-600 hover:text-red-800">
                              Disconnect Prospeo
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Prospeo API key</label>
                              <input
                                type="password"
                                value={prospeoApiKey}
                                onChange={(e) => setProspeoApiKey(e.target.value)}
                                placeholder="Your Prospeo API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">Find it in your Prospeo dashboard under API.</p>
                            </div>
                            <div className="bg-indigo-50 border border-indigo-200 rounded-md p-3">
                              <p className="text-xs text-indigo-900">
                                <strong>How it works:</strong> verified mobile numbers (with the work email included free) and verified work emails, instantly. Misses cost nothing. Prospeo doesn't provide personal emails.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveProspeoKey}
                              disabled={prospeoSaving || !prospeoApiKey.trim()}
                              className="w-full px-4 py-2 bg-gradient-to-r from-indigo-600 to-violet-600 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {prospeoSaving ? 'Connecting...' : 'Connect Prospeo'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Lusha Modal Content */}
                    {integrationModal === 'lusha' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your Lusha account for instant contact enrichment. Find work emails, personal emails, and phone numbers using your Lusha credits.
                        </p>

                        {lushaLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
                          </div>
                        ) : !lushaHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">
                              You need to be part of a team to configure Lusha. Create or join a team first.
                            </p>
                          </div>
                        ) : !lushaCanConfigure ? (
                          <div className="space-y-3">
                            {lushaConnected ? (
                              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                                Your team has connected Lusha. Contact enrichment will use Lusha with instant results.
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                Only team admins can configure the Lusha integration. Contact your team admin to set this up.
                              </p>
                            )}
                          </div>
                        ) : lushaConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Lusha is connected and active. Contact enrichment will return results instantly. Credits are billed directly to your Lusha account.
                            </p>
                            <button
                              onClick={handleDisconnectLusha}
                              disabled={lushaSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Lusha
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Lusha API Key
                              </label>
                              <input
                                type="password"
                                value={lushaApiKey}
                                onChange={(e) => setLushaApiKey(e.target.value)}
                                placeholder="Your Lusha API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Find your API key in Lusha under Settings → API
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Key benefits:</strong> Instant results, work and personal emails, mobile and direct phone numbers. Credits are per data point.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveLushaKey}
                              disabled={lushaSaving || !lushaApiKey.trim()}
                              className="w-full px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {lushaSaving ? 'Connecting...' : 'Connect Lusha'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Apollo Modal Content */}
                    {integrationModal === 'apollo' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your Apollo account for instant contact enrichment. Find work emails, personal emails, and phone numbers using your Apollo credits.
                        </p>

                        {apolloLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : !apolloHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">
                              You need to be part of a team to configure Apollo. Create or join a team first.
                            </p>
                          </div>
                        ) : !apolloCanConfigure ? (
                          <div className="space-y-3">
                            {apolloConnected ? (
                              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                                Your team has connected Apollo. Contact enrichment will use Apollo with instant results.
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                Only team admins can configure the Apollo integration. Contact your team admin to set this up.
                              </p>
                            )}
                          </div>
                        ) : apolloConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              Apollo is connected and active. Contact enrichment will return results instantly. Credits are billed directly to your Apollo account.
                            </p>
                            <button
                              onClick={handleDisconnectApollo}
                              disabled={apolloSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Apollo
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Apollo API Key
                              </label>
                              <input
                                type="password"
                                value={apolloApiKey}
                                onChange={(e) => setApolloApiKey(e.target.value)}
                                placeholder="Your Apollo API key"
                                className="input text-sm"
                              />
                              <p className="mt-1 text-xs text-gray-500">
                                Find your API key in Apollo under Settings → Integrations → API
                              </p>
                            </div>
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Key benefits:</strong> Instant results, work and personal emails, mobile phone numbers, and bulk enrichment (10 per request). Credits are billed to your Apollo account.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveApolloKey}
                              disabled={apolloSaving || !apolloApiKey.trim()}
                              className="w-full px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {apolloSaving ? 'Connecting...' : 'Connect Apollo'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* ZoomInfo Modal Content */}
                    {integrationModal === 'zoominfo' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your ZoomInfo account for instant contact enrichment. Find work emails, direct phone numbers, and mobile numbers using your ZoomInfo credits.
                        </p>

                        {zoomInfoLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : !zoomInfoHasTeam ? (
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
                            <p className="text-sm text-amber-800">
                              You need to be part of a team to configure ZoomInfo. Create or join a team first.
                            </p>
                          </div>
                        ) : !zoomInfoCanConfigure ? (
                          <div className="space-y-3">
                            {zoomInfoConnected ? (
                              <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                                Your team has connected ZoomInfo. Contact enrichment will use ZoomInfo with instant results.
                              </p>
                            ) : (
                              <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                                Only team admins can configure the ZoomInfo integration. Contact your team admin to set this up.
                              </p>
                            )}
                          </div>
                        ) : zoomInfoConnected ? (
                          <div className="space-y-3">
                            <p className="text-sm text-green-700 bg-green-50 p-3 rounded-md">
                              ZoomInfo is connected and active. Contact enrichment will return results instantly. Credits are billed directly to your ZoomInfo account.
                            </p>
                            <button
                              onClick={handleDisconnectZoomInfo}
                              disabled={zoomInfoSaving}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect ZoomInfo
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {/* Auth method toggle */}
                            <div className="flex gap-2 p-1 bg-gray-100 rounded-md">
                              <button
                                type="button"
                                onClick={() => setZoomInfoAuthMethod('basic')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${zoomInfoAuthMethod === 'basic' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
                              >
                                Username &amp; Password
                              </button>
                              <button
                                type="button"
                                onClick={() => setZoomInfoAuthMethod('pki')}
                                className={`flex-1 px-3 py-1.5 text-xs font-medium rounded ${zoomInfoAuthMethod === 'pki' ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}
                              >
                                API Key
                              </button>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                ZoomInfo Username
                              </label>
                              <input
                                type="text"
                                value={zoomInfoUsername}
                                onChange={(e) => setZoomInfoUsername(e.target.value)}
                                placeholder="Your ZoomInfo username/email"
                                className="input text-sm"
                              />
                            </div>

                            {zoomInfoAuthMethod === 'basic' ? (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  ZoomInfo Password
                                </label>
                                <input
                                  type="password"
                                  value={zoomInfoPassword}
                                  onChange={(e) => setZoomInfoPassword(e.target.value)}
                                  placeholder="Your ZoomInfo password"
                                  className="input text-sm"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                  Your credentials authenticate with the ZoomInfo API. Find them in your ZoomInfo Admin Portal under Integrations. If ZoomInfo says your account isn't authorised for API access, use the <strong>API Key</strong> option instead.
                                </p>
                              </div>
                            ) : (
                              <>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Client ID
                                  </label>
                                  <input
                                    type="text"
                                    value={zoomInfoClientId}
                                    onChange={(e) => setZoomInfoClientId(e.target.value)}
                                    placeholder="Your ZoomInfo API Client ID"
                                    className="input text-sm"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Private Key
                                  </label>
                                  <textarea
                                    value={zoomInfoPrivateKey}
                                    onChange={(e) => setZoomInfoPrivateKey(e.target.value)}
                                    placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                                    rows={5}
                                    className="input text-sm font-mono"
                                  />
                                  <p className="mt-1 text-xs text-gray-500">
                                    Paste the full private key (including the BEGIN/END lines) issued by ZoomInfo alongside your Client ID. Used to authenticate via ZoomInfo's PKI method for enterprise/API-key accounts.
                                  </p>
                                </div>
                              </>
                            )}

                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                <strong>Key benefits:</strong> Instant results, work emails, direct and mobile phone numbers, and bulk enrichment (25 per request). Credits are billed to your ZoomInfo account.
                              </p>
                            </div>
                            <button
                              onClick={handleSaveZoomInfoCredentials}
                              disabled={zoomInfoSaving || !zoomInfoUsername.trim() || (zoomInfoAuthMethod === 'basic' ? !zoomInfoPassword.trim() : (!zoomInfoClientId.trim() || !zoomInfoPrivateKey.trim()))}
                              className="w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-md hover:opacity-90 disabled:bg-gray-400 disabled:from-gray-400 disabled:to-gray-400 text-sm"
                            >
                              {zoomInfoSaving ? 'Connecting...' : 'Connect ZoomInfo'}
                            </button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Vincere Modal Body */}
                    {integrationModal === 'vincere' && (
                      <>
                        <p className="text-gray-600 text-sm mb-5">
                          Connect your Vincere account to enrich and refresh candidate and contact records directly from MarketMapr. Keep your CRM data up to date without manual effort.
                        </p>
                        {vincereLoading ? (
                          <div className="text-center py-4">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : vincereConnected ? (
                          <div className="space-y-4">
                            <div className="bg-green-50 border border-green-200 rounded-md p-4">
                              <p className="text-sm text-green-800 font-medium">Connected to <strong>{vincereTenant}.vincere.io</strong></p>
                              <p className="text-xs text-green-700 mt-1">You can now pull and enrich records from the Vincere tab in your dashboard.</p>
                            </div>
                            <button
                              onClick={handleDisconnectVincere}
                              className="text-sm text-red-600 hover:text-red-800"
                            >
                              Disconnect Vincere
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                              <p className="text-xs text-blue-800">
                                You'll need your Vincere tenant name, API key, and an OAuth client ID from your Vincere admin. Go to <strong>Vincere Admin → API Access</strong> to find these.
                              </p>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Tenant name</label>
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={vincereTenant}
                                  onChange={(e) => setVincereTenant(e.target.value)}
                                  placeholder="myagency"
                                  className="flex-1 input text-sm"
                                />
                                <span className="text-sm text-gray-400 whitespace-nowrap">.vincere.io</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">API key</label>
                              <input
                                type="password"
                                value={vincereApiKey}
                                onChange={(e) => setVincereApiKey(e.target.value)}
                                placeholder="x-api-key value"
                                className="input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">OAuth client ID</label>
                              <input
                                type="text"
                                value={vincereClientId}
                                onChange={(e) => setVincereClientId(e.target.value)}
                                placeholder="Client ID from Vincere admin"
                                className="input text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                OAuth client secret <span className="text-gray-400 font-normal">(if required)</span>
                              </label>
                              <input
                                type="password"
                                value={vincereClientSecret}
                                onChange={(e) => setVincereClientSecret(e.target.value)}
                                placeholder="Optional"
                                className="input text-sm"
                              />
                            </div>
                            <button
                              onClick={handleConnectVincere}
                              disabled={vincereSaving || !vincereTenant.trim() || !vincereApiKey.trim() || !vincereClientId.trim()}
                              className="w-full px-4 py-2 bg-[#1a2b5e] text-white rounded-md hover:bg-[#243a80] disabled:bg-gray-400 text-sm"
                            >
                              {vincereSaving ? 'Redirecting to Vincere...' : 'Connect Vincere'}
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Credit Usage & Limits Tab */}
        {activeTab === 'credit-usage' && (
          <CreditUsage />
        )}

        {/* Account Tab */}
        {activeTab === 'account' && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold mb-6 tracking-tight">Account Settings</h2>

            {/* White-label report branding */}
            <ReportBrandingSettings />

            {/* Change Password Section */}
            <div className="max-w-md">
              <h3 className="text-lg font-medium mb-4">Change Password</h3>

              {passwordError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200/80 rounded-xl">
                  <p className="text-red-800 text-sm">{passwordError}</p>
                </div>
              )}

              {passwordSuccess && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200/80 rounded-xl">
                  <p className="text-green-800 text-sm">Password changed successfully!</p>
                </div>
              )}

              <form onSubmit={async (e) => {
                e.preventDefault();
                setPasswordError(null);
                setPasswordSuccess(false);

                if (newPassword !== confirmPassword) {
                  setPasswordError('New passwords do not match');
                  return;
                }

                if (newPassword.length < 6) {
                  setPasswordError('New password must be at least 6 characters');
                  return;
                }

                setPasswordChanging(true);
                try {
                  const response = await fetch('/api/auth/change-password', {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ currentPassword, newPassword })
                  });

                  const data = await response.json();

                  if (!response.ok) {
                    throw new Error(data.error || 'Failed to change password');
                  }

                  setPasswordSuccess(true);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                } catch (err: any) {
                  setPasswordError(err.message);
                } finally {
                  setPasswordChanging(false);
                }
              }} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Current Password
                  </label>
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                    className="input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="input"
                  />
                  <p className="mt-1 text-xs text-gray-500">Minimum 6 characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="input"
                  />
                </div>
                <button
                  type="submit"
                  disabled={passwordChanging || !currentPassword || !newPassword || !confirmPassword}
                  className="w-full btn-primary"
                >
                  {passwordChanging ? 'Changing Password...' : 'Change Password'}
                </button>
              </form>
            </div>
          </div>
        )}

        {activeTab === 'training' && user?.role === 'admin' && (
          <TrainingAdmin />
        )}

        {activeTab === 'feature-requests' && user?.id === 1 && (
          <AdaFeatureRequestsManager />
        )}

        {activeTab === 'announcements' && user?.id === 1 && (
          <AnnouncementManager />
        )}
      </div>

      {/* Cancel Subscription Confirmation Modal */}
      {showCancelModal && (
        <div className="modal-backdrop">
          <div className="modal-dialog max-w-md w-full mx-4 p-6 animate-scale-in">
            <h3 className="text-lg font-semibold text-gray-900 mb-2 tracking-tight">Cancel Subscription</h3>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel your MarketMapr Pro subscription? You'll continue to have access until the end of your current billing period.
            </p>
            <div className="bg-amber-50 border border-amber-200/80 rounded-xl p-3 mb-6">
              <p className="text-amber-800 text-sm">
                After cancellation, you will lose access to all MarketMapr Pro features including projects, data enrichment, and team collaboration.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={cancelLoading}
                className="btn-secondary"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={cancelLoading}
                className="btn-danger"
              >
                {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
